import { DefaultDevices, DeviceType } from 'icreate/common/models/device';
import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChildren,
} from '@angular/core';
import { Model, VesselEquipment } from 'icreate/common/models/device';
import { OpsUnit } from 'icreate/common/models/ops-unit';
import { CeuUnit } from 'icreate/common/models/ceu-unit';
import { Subject, fromEvent, lastValueFrom, takeUntil } from 'rxjs';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-multiple-device-list',
    templateUrl: './multiple-device-list.component.html',
    styleUrl: './multiple-device-list.component.css',
    standalone: false,
})
export class MultipleDeviceListComponent implements OnInit, OnDestroy {
    @Input() models: Model[];
    @Input() units: OpsUnit[] | CeuUnit[] | string[] | any[];
    @Input() vesselEquipment: VesselEquipment[];
    @Input() initialVesselEquipment: VesselEquipment[];
    @Input() vesselId: string;
    @Input() deviceType: DeviceType;
    @ViewChildren('optionsCol') optionsCol: QueryList<ElementRef>;
    @Output() onVesselEquipmentChanged: EventEmitter<VesselEquipment[]> = new EventEmitter();
    @Output() onCloseBulkEdit: EventEmitter<void> = new EventEmitter();

    openedUnitOptions;
    optionsTop: string;
    isBulkEditing: boolean = false;

    isBulkEditAll: boolean = false;
    bulkEditModel: Model = null;

    private expandedUnitKeys = new Set<string | number>();

    private destroy$: Subject<void> = new Subject();

    constructor(private dialog: MatDialog) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        const isInsideAny = this.optionsCol.toArray().some(el => el.nativeElement.contains(event.target));

        if (!isInsideAny) {
            this.openedUnitOptions = null;
        }
    }

    ngOnInit(): void {
        this.units = this.units ?? [];
        this.units.sort((a, b) => a?.index - b?.index);

        const scrollContainer = document.querySelector('.system-setting-scroll-parent');
        if (scrollContainer) {
            fromEvent(scrollContainer, 'scroll')
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => {
                    this.openedUnitOptions = null;
                });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private getUniqueKey(unit): any {
        let uniqueKey = unit?.id;
        if (this.deviceType == DefaultDevices.IO || !uniqueKey) uniqueKey = unit;
        return uniqueKey;
    }

    handleModelSelected(selectedModel: Model, unit) {
        const uniqueKey = this.getUniqueKey(unit);

        if (this.vesselEquipment && this.vesselEquipment.length > 0) {
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == uniqueKey);
            if (unitIndex >= 0) {
                this.vesselEquipment[unitIndex].equipmentModelId = selectedModel.id;
                return;
            }
        } else this.vesselEquipment = [];

        if (this.initialVesselEquipment && this.initialVesselEquipment.length > 0) {
            const initialEquipment = this.initialVesselEquipment.find(equipment => equipment.designCode == uniqueKey);
            if (initialEquipment) {
                this.vesselEquipment.push({
                    ...initialEquipment,
                    equipmentModelId: selectedModel.id,
                });
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
                return;
            }
        }

        this.vesselEquipment.push({
            name: this.deviceType,
            equipmentModelId: selectedModel.id,
            vesselId: this.vesselId,
            designCode: uniqueKey,
        });

        this.onVesselEquipmentChanged.emit(this.vesselEquipment);
    }

    isIoDevice() {
        return this.deviceType == DefaultDevices.IO;
    }

    getIndex(unit) {
        if (this.deviceType == DefaultDevices.IO) return unit;
        return unit.index;
    }

    getUnitModel(unit) {
        const uniqueKey = this.getUniqueKey(unit);
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == uniqueKey
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel;
    }

    getModelItemCode(unit): string {
        const uniqueKey = this.getUniqueKey(unit);
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == uniqueKey
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel?.code;
    }

    getModelDescription(unit): string {
        const uniqueKey = this.getUniqueKey(unit);
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return null;
        if (!this.models && this.models.length == 0) return null;
        const selectedEquipment = this.vesselEquipment.find(
            equipment => equipment.name == this.deviceType && equipment.designCode == uniqueKey
        );

        if (!selectedEquipment) return null;
        const selectedModel = this.models.find(model => model.id == selectedEquipment.equipmentModelId);
        if (!selectedModel) return null;
        return selectedModel?.description;
    }

    isUnitExpanded(unit): boolean {
        return this.expandedUnitKeys.has(this.getUniqueKey(unit));
    }

    setUnitExpanded(unit, expanded: boolean): void {
        const uniqueKey = this.getUniqueKey(unit);
        if (expanded) {
            this.expandedUnitKeys.add(uniqueKey);
        } else {
            this.expandedUnitKeys.delete(uniqueKey);
        }
    }

    openUnitOptions(unit, index) {
        if (this.openedUnitOptions == unit) {
            this.openedUnitOptions = null;
            return;
        }
        this.updatePosition(index);
        this.openedUnitOptions = unit;
    }

    async deleteUnit(unit) {
        const uniqueKey = this.getUniqueKey(unit);
        const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == uniqueKey);

        if (unitIndex >= 0) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.SIMPLE,
                    title: 'device-list.delete.title',
                    title2: 'device-list.delete.title2',
                    discardButton: 'dlg.cancel',
                    saveButton: 'dlg.confirm',
                    width: '452px',
                },
                disableClose: true,
            });
            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (this.vesselEquipment && this.vesselEquipment.length > 0) {
                    this.vesselEquipment.splice(unitIndex, 1);
                }
                this.expandedUnitKeys.delete(uniqueKey);
                this.onVesselEquipmentChanged.emit(this.vesselEquipment);
            }
        }
    }

    startBulkEditing() {
        this.isBulkEditAll = false;
        this.bulkEditModel = null;
        this.units.forEach(unit => {
            unit.checked = false;
        });
        this.isBulkEditing = true;
    }

    cancelBulkEditing() {
        this.isBulkEditAll = false;
        this.bulkEditModel = null;
        this.units.forEach(unit => {
            if (unit?.checked) unit.checked = false;
        });
        this.isBulkEditing = false;
    }

    isApplyDisabled() {
        if (!this.isBulkEditing || !this.bulkEditModel) return true;
        return this.units.every(unit => !unit.checked);
    }

    applyBulkEdit() {
        if (this.isApplyDisabled()) return;
        if (!this.vesselEquipment) this.vesselEquipment = [];
        this.units.forEach(unit => {
            if (!unit.checked) return;
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == unit.id);
            if (unitIndex >= 0) {
                this.vesselEquipment[unitIndex].equipmentModelId = this.bulkEditModel.id;
            } else {
                this.vesselEquipment.push({
                    name: this.deviceType,
                    equipmentModelId: this.bulkEditModel.id,
                    vesselId: this.vesselId,
                    designCode: unit.id,
                });
            }
        });
        this.onVesselEquipmentChanged.emit(this.vesselEquipment);
        this.onCloseBulkEdit.emit();
        this.cancelBulkEditing();
    }

    showClearButton() {
        if (!this.vesselEquipment || this.vesselEquipment.length == 0) return false;

        return this.units.some(unit => {
            if (!unit.checked) return false;
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == unit.id);
            return unitIndex >= 0;
        });
    }

    checkAll() {
        if (!this.isBulkEditing) return;
        if (this.isBulkEditAll) {
            this.units = this.units.map(unit => {
                unit.checked = false;
                return unit;
            });
            this.isBulkEditAll = false;
            return;
        }
        this.units = this.units.map(unit => {
            unit.checked = true;
            return unit;
        });
        this.isBulkEditAll = true;
    }

    checkUnit(unit) {
        if (!this.isBulkEditing) return;
        unit.checked = !unit.checked;
        this.isBulkEditAll = this.units.every(un => un.checked);
    }

    handleBulkModelSelected(selectedModel: Model) {
        this.bulkEditModel = selectedModel;
    }

    clearAll() {
        this.units.forEach(unit => {
            if (!unit.checked) return;
            const unitIndex = this.vesselEquipment.findIndex(equipment => equipment.designCode == unit.id);
            if (unitIndex >= 0) {
                this.vesselEquipment.splice(unitIndex, 1);
            }
        });

        this.onVesselEquipmentChanged.emit(this.vesselEquipment);

        this.units = this.units.map(unit => {
            unit.checked = false;
            return unit;
        });
        this.isBulkEditAll = false;
    }

    private updatePosition(index) {
        if (!this.optionsCol) return;
        const optionsColArray = this.optionsCol.toArray();
        const selectedUnit = optionsColArray ? optionsColArray[index] : null;
        if (!selectedUnit) return;
        const unitPosition = selectedUnit.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;

        this.optionsTop = unitPosition.bottom + scrollTop + 'px';
    }
}
